/**
 * ChartRenderer - Renders MUI X Charts from agent actions
 */
import { useState } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { Save } from 'lucide-react';
import { SaveToDashboardModal } from './SaveToDashboardModal';

interface ChartData {
  label: string;
  value: number;
}

interface ChartAction {
  type: 'show_chart';
  chart_type: 'bar' | 'line' | 'pie';
  data: ChartData[];
  title: string;
}

interface ChartRendererProps {
  action: ChartAction;
}

export function ChartRenderer({ action }: ChartRendererProps) {
  const { chart_type, data, title } = action;
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 2, my: 1 }}>
        <Typography color="text.secondary">No data available</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, my: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Tooltip title="Save to Dashboard">
          <IconButton size="small" onClick={() => setSaveModalOpen(true)}>
            <Save size={16} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ width: '100%', height: 300 }}>
        {chart_type === 'bar' && <BarChart xAxis={[{ scaleType: 'band', data: data.map((d) => d.label) }]} series={[{ data: data.map((d) => d.value) }]} height={280} />}
        {chart_type === 'pie' && (
          <PieChart
            series={[
              {
                data: data.map((d, i) => ({ id: i, value: d.value, label: d.label })),
              },
            ]}
            height={280}
          />
        )}
        {chart_type === 'line' && <LineChart xAxis={[{ scaleType: 'point', data: data.map((d) => d.label) }]} series={[{ data: data.map((d) => d.value) }]} height={280} />}
      </Box>
      <SaveToDashboardModal open={saveModalOpen} onClose={() => setSaveModalOpen(false)} chartType={chart_type} chartData={data} chartTitle={title} />
    </Paper>
  );
}
